import React from 'react';

/**
 * A trend shape for a KPI tile — deliberately axis-less and unlabelled.
 *
 * Its whole job is to answer "which way, and how steadily", which the tile's
 * own number and delta then quantify. Adding a y-axis to something 28px tall
 * makes it look like a chart you can read values off, which you can't; if the
 * values matter, the page owes you a real LineTrend below.
 *
 * A null value breaks the line rather than drawing through it — a missing
 * nightly rollup is not a zero.
 */
export default function Sparkline({ values, height = 28, color = '#2a78d6', className = '' }) {
  const points = (values || []).filter((v) => v != null);
  if (points.length < 2) {
    return <div className={`h-[${height}px] ${className}`} aria-hidden="true" />;
  }

  const W = 120;
  const H = height;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const stepX = W / (values.length - 1);

  let d = '';
  let open = false;
  values.forEach((v, i) => {
    if (v == null) { open = false; return; }
    const x = i * stepX;
    const y = H - ((v - min) / span) * (H - 4) - 2;
    d += `${open ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)} `;
    open = true;
  });

  const lastIdx = values.length - 1 - [...values].reverse().findIndex((v) => v != null);
  const lastVal = values[lastIdx];
  const lastX = lastIdx * stepX;
  const lastY = H - ((lastVal - min) / span) * (H - 4) - 2;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={`w-full ${className}`}
      style={{ height: H }}
      role="img"
      aria-hidden="true"
    >
      <path d={d.trim()} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={lastX} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
}
